import { createStackNavigator } from '@react-navigation/stack';
import { Module } from '../Routes';

import moment from 'moment';

import {
    faClipboardList,
    faCar,
    faShield,
    faBug,
    faFileSignature,
    faGear,
    faIdBadge,
    faRoute,
    faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { AppAgentParamList } from '../typings/Params';
import { AppAgentSchemas } from '../schemas/agent-app';
import { OccurrenceProvider } from '../context/Occurrence';
import { faMemoCircleInfo, faMemoCircleCheck } from '@fortawesome/pro-regular-svg-icons';

// App Agent
import AgentSignatures from '../pages/app-agent/AgentSignatures';
import AgentUnits from '../pages/app-agent/AgentUnits/AgentUnits';
import CortexFinder from '../pages/app-agent/CortexFinder/CortexFinder';
import AgentOccurrenceDetails from '../pages/app-agent/Occurrences/AgentOccurrenceDetails';
import AgentOccurrences from '../pages/app-agent/Occurrences/AgentOccurrences';
import PersonOfInterest from '../pages/app-agent/PersonOfInterest';
import OpenVacancies from '../pages/app-agent/Deac/OpenVacancies/OpenVacancies';
import VacancyDetailsApply from '../pages/app-agent/Deac/OpenVacancies/VacancyDetailsApply';
import Candidacies from '../pages/app-agent/Deac/Candidacies/Candidacies';
import VacancyDetailsCancel from '../pages/app-agent/Deac/Candidacies/VacancyDetailsCancel';
import Debugger from '../pages/app-agent/Debugger/Debugger';
import Services from '../pages/app-agent/Services/Services';
import LprEvents from '../pages/app-agent/LprEvents/LprEvents';
import PolicePatrolSchedule from '../pages/app-agent/PolicePatrolSchedule/PolicePatrolSchedule';
import PolicePatrolScheduleDetails from '../pages/app-agent/PolicePatrolSchedule/PolicePatrolScheduleDetails';


export const appAgentModule: Module<'AppAgent'> = {
    path: '/app-agent',
    stackNavigator: createStackNavigator<AppAgentParamList>(),
    canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.receive_occurrences),
    icon: require('../../assets/icon_app_agent.png'),
    logo: require('../../assets/logo_app_agent.png'),
    label: 'appAgent',
    navigate: (navigation) => navigation.navigate('AppAgent'),
    schemas: AppAgentSchemas,
    providers: OccurrenceProvider,
    screens: {
        AgentOccurrences: {
            path: 'occurrences',
            selectedItem: 'occurrences',
            component: AgentOccurrences,
            label: 'occurrences',
            canAccess: (_, currentApp) => Boolean(currentApp.isNativeAgentApp || !currentApp.isAgentApp),
            accessInformation: {
                icon: faClipboardList,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'AgentOccurrences' }),
            }
        },
        AgentOccurrenceDetails: {
            path: 'occurrences/:occurrenceId',
            selectedItem: 'occurrences',
            component: AgentOccurrenceDetails,
            label: 'occurrences',
            canAccess: (_, currentApp) => Boolean(currentApp.isNativeAgentApp || !currentApp.isAgentApp),
        },
        AgentUnits: {
            path: 'units',
            selectedItem: 'units',
            component: AgentUnits,
            label: 'teams',
            canAccess: (_, currentApp) => Boolean(currentApp.isNativeAgentApp || !currentApp.isAgentApp),
            accessInformation: {
                icon: faShield,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'AgentUnits' }),
            }
        },
        PolicePatrolSchedule: {
            path: 'police-patrol-schedule',
            selectedItem: 'police-patrol-schedule',
            canAccess: (user, currentApp) => Boolean(user?.permissions.receive_occurrences && (currentApp.isNativeAgentApp || !currentApp.isAgentApp)),
            component: PolicePatrolSchedule,
            label: 'policePatrolSchedule',
            accessInformation: {
                icon: faRoute,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'PolicePatrolSchedule' }),
            },
            ignorePadding: true
        },
        PolicePatrolScheduleDetails: {
            path: 'police-patrol-schedule/details/:activityId?',
            selectedItem: 'police-patrol-schedule',
            canAccess: (user, currentApp) => Boolean(user?.permissions.receive_occurrences && (currentApp.isNativeAgentApp || !currentApp.isAgentApp)),
            component: PolicePatrolScheduleDetails,
            label: 'policePatrolScheduleDetails',
            ignorePadding: true,
        },
        CortexFinder: {
            path: 'cortex-finder',
            selectedItem: 'cortex-finder',
            canAccess: (user) => Boolean(
                user?.isAdmin ||
                user?.permissions.search_people_cortex ||
                user?.permissions.search_cortex_any_time
            ),
            component: CortexFinder,
            label: 'searchOnCortex',
            accessInformation: {
                icon: faSearch,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'CortexFinder' }),
            }
        },
        PersonOfInterest: {
            path: 'person-of-interest',
            selectedItem: 'person-of-interest',
            canAccess: (user, currentApp) => Boolean(currentApp.isParentApp),
            component: PersonOfInterest,
            label: 'personOfInterest',
            accessInformation: {
                icon: faIdBadge,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'PersonOfInterest' }),
            },
        },
        AgentSignatures: {
            path: 'signatures',
            selectedItem: 'signatures',
            canAccess: () => true,
            component: AgentSignatures,
            label: 'signatures',
            accessInformation: {
                icon: faFileSignature,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'AgentSignatures' }),
            },
        },
        OpenVacancies: {
            path: 'open-vacancies',
            selectedItem: 'open-vacancies',
            canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
            component: OpenVacancies,
            label: 'openVacancies',
            initialParams: () => ({
                showOnlyAvailableVacancies: true,
            }),
            accessInformation: {
                icon: faMemoCircleInfo,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'OpenVacancies' }),
            },
        },
        VacancyDetailsApply: {
            path: 'vacancy-details/apply/:id',
            selectedItem: 'open-vacancies',
            canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
            component: VacancyDetailsApply,
            label: 'vacancyDetails',
        },
        Candidacies: {
            path: 'my-candidacies',
            selectedItem: 'my-candidacies',
            canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
            component: Candidacies,
            label: 'myCandidacies',
            initialParams: () => ({
                beginIn: moment().startOf('year').valueOf(),
                endIn: moment().endOf('year').valueOf(),
            }),
            accessInformation: {
                icon: faMemoCircleCheck,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'Candidacies' }),
            },
        },
        VacancyDetailsCancel: {
            path: 'vacancy-details/cancel/:id',
            selectedItem: 'my-candidacies',
            canAccess: (user) => Boolean(user?.permissions.app_agent_deac_access),
            component: VacancyDetailsCancel,
            label: 'vacancyDetails',
        },
        Debugger: {
            path: 'debugger',
            selectedItem: 'debugger',
            canAccess: (_, currentApp) => currentApp.isNativeAgentApp,
            component: Debugger,
            label: 'debugger',
            initialParams: () => ({
                tags: [],
                subTags: [],
            }),
            accessInformation: {
                icon: faBug,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'Debugger' }),
            },
        },
        Services: {
            path: 'services',
            selectedItem: 'services',
            canAccess: (_, currentApp) => currentApp.isNativeAgentApp,
            component: Services,
            label: 'services',
            accessInformation: {
                icon: faGear,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'Services' }),
            },

        },
        LprEvents: {
            path: 'lpr-events',
            selectedItem: 'lpr-events',
            canAccess: (user, currentApp) => Boolean(currentApp.isNativeAgentApp && user?.permissions.app_agent_manage_services),
            component: LprEvents,
            label: 'lprEvents',
            accessInformation: {
                icon: faCar,
                navigate: (navigation) => navigation.navigate('AppAgent', { screen: 'LprEvents' }),
            },

        }
    }
};
